import type { StorageService } from '@repo/core';

/**
 * Web storage implementation using localStorage.
 *
 * To use on Capacitor mobile, replace this with @capacitor/preferences:
 * ```ts
 * import { Preferences } from '@capacitor/preferences';
 *
 * export class CapacitorStorageService implements StorageService {
 *   async get<T>(key: string) { ... }
 *   async set<T>(key: string, value: T) { ... }
 * }
 * ```
 * Then register CapacitorStorageService in the mobile app's DI container.
 */
export class LocalStorageService implements StorageService {
  async get<T>(key: string): Promise<T | null> {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    localStorage.setItem(key, JSON.stringify(value));
  }

  async remove(key: string): Promise<void> {
    localStorage.removeItem(key);
  }

  async clear(): Promise<void> {
    localStorage.clear();
  }
}
